/**
 * BLRRD Persistence — Thumbnail generator.
 *
 * Renders a fitted JPEG thumbnail from any source the editor can supply:
 * Blob (original file bytes), Canvas (rendered preview), or HTMLImageElement.
 *
 * Default size 256×256, quality 0.82 — about 8–20 KB per image, so a
 * 100-image library stays well under 2 MB. Privacy-friendly: never leaves
 * the browser.
 */

const DEFAULT_MAX = 256;
const DEFAULT_QUALITY = 0.82;

function fittedSize(srcW, srcH, max) {
    if (srcW <= max && srcH <= max) return { w: srcW, h: srcH };
    if (srcW > srcH) {
        return { w: max, h: Math.max(1, Math.round(srcH * max / srcW)) };
    }
    return { w: Math.max(1, Math.round(srcW * max / srcH)), h: max };
}

function canvasToBlob(canvas, mime, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob); else reject(new Error('canvas.toBlob returned null'));
        }, mime, quality);
    });
}

/**
 * Generate a thumbnail Blob from a Blob (original image bytes).
 *
 * Codex Fix #4: never throw on decode failure — return null so callers can
 * proceed without a thumbnail (the Library shows a generic placeholder for
 * un-decodable formats like HEIC-on-Chrome or truncated bytes).
 *
 * Decode strategy:
 *   1. createImageBitmap on the Blob (off-main-thread, fastest).
 *   2. HTMLImageElement via Object URL (covers HEIC where browser can render
 *      to image but not to bitmap; covers Firefox-private edge cases).
 *   3. Bail to null.
 */
export async function thumbnailFromBlob(blob, opts = {}) {
    const max = opts.max || DEFAULT_MAX;
    const q = opts.quality != null ? opts.quality : DEFAULT_QUALITY;

    /* Path 1 — createImageBitmap. */
    try {
        const bitmap = await createImageBitmap(blob);
        try {
            const { w, h } = fittedSize(bitmap.width, bitmap.height, max);
            const canvas = (typeof OffscreenCanvas === 'function')
                ? new OffscreenCanvas(w, h)
                : Object.assign(document.createElement('canvas'), { width: w, height: h });
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(bitmap, 0, 0, w, h);
            if (canvas.convertToBlob) return await canvas.convertToBlob({ type: 'image/jpeg', quality: q });
            return await canvasToBlob(canvas, 'image/jpeg', q);
        } finally {
            bitmap.close && bitmap.close();
        }
    } catch { /* fall through to HTMLImageElement */ }

    /* Path 2 — HTMLImageElement. Wrap with object URL; revoke on settle. */
    const url = URL.createObjectURL(blob);
    try {
        return await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                try {
                    const { w, h } = fittedSize(img.naturalWidth || img.width,
                                                img.naturalHeight || img.height,
                                                max);
                    const canvas = document.createElement('canvas');
                    canvas.width = w; canvas.height = h;
                    const ctx = canvas.getContext('2d');
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(img, 0, 0, w, h);
                    canvasToBlob(canvas, 'image/jpeg', q).then(resolve, () => resolve(null));
                } catch { resolve(null); }
            };
            img.onerror = () => resolve(null);
            img.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

/**
 * Generate a thumbnail Blob from a rendered HTMLCanvasElement (the editor's
 * preview canvas after applying grade). Use this for project thumbnails so
 * the library shows the *edited* look, not the source.
 */
export async function thumbnailFromCanvas(canvas, opts = {}) {
    const max = opts.max || DEFAULT_MAX;
    const q = opts.quality != null ? opts.quality : DEFAULT_QUALITY;
    const { w, h } = fittedSize(canvas.width, canvas.height, max);
    const out = document.createElement('canvas');
    out.width = w; out.height = h;
    const ctx = out.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(canvas, 0, 0, w, h);
    return canvasToBlob(out, 'image/jpeg', q);
}
